import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  useToast,
} from '@ais-ui/ui-core';
import { WMSInfo, useSaveSettings } from '@ais-ui/websockets';
import React, { useState } from 'react';
import { deleteWmsToSettings } from '../../WMSLayers/useWMSSettings';
import { useSettings } from '../SettingsProvider';
import { useLocalization } from '@ais-ui/locale-management';

interface WMSDeleteServiceProps {
  wmsLayer: WMSInfo;
}

export const WMSDeleteService = ({ wmsLayer }: WMSDeleteServiceProps) => {
  const { messages } = useLocalization();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { toast } = useToast();
  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const onDelete = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    const settingsToSave = deleteWmsToSettings(wmsLayer, settings);

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        setIsSubmitting(false);
        setShowDialog(false);
        toast({
          className: 'delete-wms-service-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsWmsSuccessDeleteMessage,
        });
      },
      onError: () => {
        setIsSubmitting(false);
        setShowDialog(false);
        toast({
          className: 'delete-wms-layer-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsWmsFailedDeleteMessage,
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        <Button variant="ghost" className="text-primary hover:text-primary">
          {messages.settingsWmsDeleteButtonText}
        </Button>
      </DialogTrigger>
      <DialogContent id="delete-wms-service-dialog">
        <DialogHeader className="space-y-4">
          <DialogTitle>{messages.settingsWmsDeleteTitle}</DialogTitle>
          <DialogDescription>
            {messages.settingsWmsDeleteWarning}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onDelete}>
          <div className="grid grid-cols-[auto_minmax(50%,_1fr)] gap-y-4 gap-x-2 items-baseline">
            <Label className="font-light">
              {messages.settingsWmsDeleteNameLabel}
            </Label>
            <Label htmlFor="wms-service-name">{wmsLayer.name}</Label>
          </div>
          <DialogFooter className="p-4">
            <Button
              variant="destructive"
              type="submit"
              id="wms-service-confirm-delete-button"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? messages.genericSubmitting
                : messages.genericSubmit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
